import { uniq } from 'lodash-es'
import { Context, useQuery } from '@apollo/client'
import { FetchPolicy, gql } from '@apollo/client'

import { Locale, DropdownOption } from '@/types'
import { locales } from '@/components/App/i18n'
import { useLoggedInUser } from '@/hooks'

const QUERY = gql`
  query company($id: UUID!) {
    companyById(id: $id) {
      locale
      availableLocales
    }
  }
`

type Props = {
  fetchPolicy?: FetchPolicy
  skip?: boolean
  context?: Context
  companyId?: string
  excludeBaseLocale?: boolean
}

type Payload = {
  availableLocales: Locale[]
  localeOptions: DropdownOption[]
}

const useAvailableLocales = ({
  fetchPolicy,
  skip,
  context,
  companyId: companyIdProp,
  excludeBaseLocale = false,
}: Props = {}): Payload => {
  const loggedInUser = useLoggedInUser()

  const companyId = companyIdProp ?? loggedInUser.companyId

  const { data } = useQuery(QUERY, {
    variables: {
      id: companyId,
    },
    fetchPolicy,
    skip,
    context,
  })

  const baseLocale: Locale = data?.companyById?.locale
  const availableLocales: Locale[] = data?.companyById?.availableLocales ?? []

  const localeOptions: DropdownOption[] = availableLocales.map(locale => ({
    key: locale,
    value: locale,
    text: locales[locale],
  }))

  return {
    availableLocales: uniq([...availableLocales, baseLocale]).filter(locale =>
      excludeBaseLocale ? locale !== baseLocale : true,
    ),
    localeOptions: localeOptions.filter(option =>
      excludeBaseLocale ? option.key !== baseLocale : true,
    ),
  }
}

export default useAvailableLocales
